import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';
import {
  GridFilterModel,
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-Icon-filled.svg';
import styles from './filterStyle.module.scss';
import { useTranslation } from 'react-i18next';
type FilterTextFieldProps = {
  gridRenderHeaderFilterProps: GridRenderHeaderFilterProps;
  operator?: 'contains' | 'equal' | 'startWith' | 'endWith';
  parentClass?: string;
  applyOnEnterOnly?: boolean;
  onApplyFilter?: (columnField: string) => void;
} & TextFieldProps;

const FilterTextField = ({
  gridRenderHeaderFilterProps,
  operator,
  parentClass,
  applyOnEnterOnly = false,
  onApplyFilter,
  ...rest
}: FilterTextFieldProps) => {
  const {colDef} = gridRenderHeaderFilterProps;
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const {t} = useTranslation();

  // Find the current filters applied to this column
  const currentFieldFilters = useMemo(
    () => filterModel.items?.filter(({field}) => field === colDef.field),
    [colDef.field, filterModel.items]
  );
  // Get the current filter value
  const value = currentFieldFilters[0]?.value ?? '';
  const {inputRef} = gridRenderHeaderFilterProps;
  const [localValue, setLocalValue] = useState(value);

  const handleTextUpdate = (value: string) => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    // If there's no value, delete the filter for this column
    if (!value) {
      if (currentFieldFilters[0]) {
        apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }
      return;
    }

    // Otherwise, upsert a new filter or update an existing one
    const updatedFilterModal: GridFilterModel = {
      items: [
        {
          ...(currentFieldFilters[0] || {
            field: colDef.field,
            operatorValue: operator ?? 'contains', // runtime property
          }),
          value: value,
        },
        ...filterModel.items.filter((item) => item.field !== colDef.field),
      ],
    };
    apiRef.current?.setFilterModel(updatedFilterModal);
    onApplyFilter?.(colDef.field);
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;

      if (applyOnEnterOnly) {
        setLocalValue(val); // only update UI
        return;
      }

      handleTextUpdate(val);
    },
    [apiRef, colDef.field, currentFieldFilters]
  );

  const handleClear = useCallback(() => {
    apiRef.current.deleteFilterItem(currentFieldFilters[0]);
    if (applyOnEnterOnly) {
      setLocalValue('');
    }
  }, [apiRef, colDef.field, currentFieldFilters]);

  // Manual tab navigation in filter field
  const tabNavigation = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && event.shiftKey) {
      const previousFieldContainer = document.querySelector(
        `[tabindex="0"]`
      ) as HTMLElement;
      const previousInput = previousFieldContainer?.querySelector(
        'input'
      ) as HTMLInputElement;
      if (previousInput) {
        event.preventDefault();
        previousInput.focus();
      }
    } else if (event.key === 'Tab' && !event.shiftKey) {
      const nextFieldContainer = document.querySelector(
        `[tabindex="0"]`
      ) as HTMLElement;
      const nextInput = nextFieldContainer?.querySelector(
        'input'
      ) as HTMLInputElement;
      if (nextInput) {
        event.preventDefault();
        nextInput.focus();
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (applyOnEnterOnly) {
        handleTextUpdate(localValue); // apply filter here
      } else {
        if (value) handleTextUpdate(value);
      }
    }
  };

  useEffect(() => {
    if (applyOnEnterOnly) {
      setLocalValue(value);
    }
  }, [value, applyOnEnterOnly]);

  return (
    <TextField
      className={`${styles.aurDataGridProDefaultTextFilter} ${parentClass ?? ''}`}
      inputRef={inputRef}
      value={applyOnEnterOnly ? localValue : value}
      onChange={handleChange}
      onKeyDown={tabNavigation}
      id="search-admin"
      variant="standard"
      fullWidth
      inputProps={{
        tabIndex: 0,
        'aria-label': t('common.filterName', {field: gridRenderHeaderFilterProps.item.field}),
      }}
      InputProps={{
        endAdornment: value && (
          <InputAdornment
            position="end"
            className={styles.filterTextFieldClearButton}
          >
            <IconButton
              onClick={handleClear}
              size="small"
              tabIndex={0}
              aria-label={t('common.clearFilter', {field: gridRenderHeaderFilterProps.item.field})}
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

export default FilterTextField;
